import React, { useState, useEffect } from 'react';
import { Plus, CheckSquare, Trash2, LayoutGrid, List, Pencil } from 'lucide-react';
import { sportsApi } from '../services/sportsApi';
import SportForm from '../components/sports/SportForm';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { ToastContainer } from '../components/common/Toast';
import { useToast } from '../hooks/useToast';
import { usePreferredViewMode, STANDARD_VIEW_MODE_MAP } from '../hooks/usePreferredViewMode';
import '../styles/PageLayout.css';

interface Sport {
  id: number;
  name: string;
  description?: string;
  icon?: string;
  isActive: boolean;
  createdAt: string;
}

/**
 * Sports management page with CRUD operations
 */
const Sports: React.FC = () => {
  const [sports, setSports] = useState<Sport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = usePreferredViewMode<'list' | 'cards'>('cards', STANDARD_VIEW_MODE_MAP);
  const [searchTerm, setSearchTerm] = useState('');

  // Form and dialog states
  const [showForm, setShowForm] = useState(false);
  const [editingSport, setEditingSport] = useState<Sport | null>(null);
  const [formLoading, setFormLoading] = useState(false);

  // Delete confirmation
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [sportToDelete, setSportToDelete] = useState<Sport | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Bulk operations
  const [selectedSports, setSelectedSports] = useState<Set<number>>(new Set());
  const [bulkMode, setBulkMode] = useState(false);

  // Toast notifications
  const { toasts, removeToast, showSuccess, showError, showWarning } = useToast();

  useEffect(() => {
    loadSports();
  }, []);

  const loadSports = async () => {
    try {
      setLoading(true);
      const data = await sportsApi.getSports();
      setSports(data);
      setError(null);
    } catch (err) {
      setError('Fehler beim Laden der Sportarten');
      console.error('Error loading sports:', err);
    } finally {
      setLoading(false);
    }
  };

  // CRUD Operations
  const handleCreateSport = () => {
    setEditingSport(null);
    setShowForm(true);
  };

  const handleEditSport = (sport: Sport) => {
    setEditingSport(sport);
    setShowForm(true);
  };

  const handleSaveSport = async (data: { name: string; description?: string; icon?: string; isActive?: boolean }) => {
    try {
      setFormLoading(true);

      if (editingSport) {
        // Update existing sport
        await sportsApi.updateSport(editingSport.id, data);
        showSuccess('Sportart erfolgreich aktualisiert');
      } else {
        // Create new sport
        await sportsApi.createSport(data);
        showSuccess('Sportart erfolgreich erstellt');
      }

      setShowForm(false);
      setEditingSport(null);
      await loadSports();
    } catch (err: any) {
      const errorMessage = err.message || (editingSport ? 'Fehler beim Aktualisieren der Sportart' : 'Fehler beim Erstellen der Sportart');
      showError(errorMessage);
      console.error('Error saving sport:', err);
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteSport = async (sport: Sport) => {
    try {
      // Check if sport can be deleted
      const { canDelete } = await sportsApi.canDeleteSport(sport.id);

      if (!canDelete) {
        showWarning('Diese Sportart kann nicht gelöscht werden, da sie von anderen Datensätzen verwendet wird.');
        return;
      }

      setSportToDelete(sport);
      setShowDeleteDialog(true);
    } catch (err) {
      showError('Fehler beim Prüfen der Löschberechtigung');
      console.error('Error checking delete permission:', err);
    }
  };

  const confirmDeleteSport = async () => {
    if (!sportToDelete) return;

    try {
      setDeleteLoading(true);
      await sportsApi.deleteSport(sportToDelete.id);
      showSuccess('Sportart erfolgreich gelöscht');
      setShowDeleteDialog(false);
      setSportToDelete(null);
      await loadSports();
    } catch (err: any) {
      const errorMessage = err.message || 'Fehler beim Löschen der Sportart';
      showError(errorMessage);
      console.error('Error deleting sport:', err);
    } finally {
      setDeleteLoading(false);
    }
  };

  // Bulk operations
  const handleSelectSport = (sportId: number) => {
    const newSelected = new Set(selectedSports);
    if (newSelected.has(sportId)) {
      newSelected.delete(sportId);
    } else {
      newSelected.add(sportId);
    }
    setSelectedSports(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedSports.size === filteredSports.length) {
      setSelectedSports(new Set());
    } else {
      setSelectedSports(new Set(filteredSports.map(s => s.id)));
    }
  };

  const handleBulkDelete = () => {
    if (selectedSports.size === 0) {
      showWarning('Bitte wählen Sie mindestens eine Sportart aus');
      return;
    }
    showWarning('Bulk-Löschung ist noch nicht implementiert');
  };

  // Filter sports based on search term
  const filteredSports = sports.filter(sport =>
    sport.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (sport.description && sport.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="page-container">
        <div className="page-loading">
          <div className="page-spinner"></div>
          <p>Lade Sportarten...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Controls – two-row Menüband */}
      <div className="page-controls">
        {/* Row 1: Buttons */}
        <div className="page-controls-row">
          <button className="page-btn page-btn-create" onClick={handleCreateSport} title="Neue Sportart erstellen">
            <Plus size={14} /> Neu
          </button>
          <button
            className={`page-btn ${bulkMode ? 'page-btn-info' : 'page-btn-secondary'}`}
            onClick={() => setBulkMode(!bulkMode)}
            title="Mehrfachauswahl aktivieren"
          >
            <CheckSquare size={14} /> Auswahl
          </button>
          {bulkMode && selectedSports.size > 0 && (
            <button className="page-btn page-btn-danger" onClick={handleBulkDelete} title={`${selectedSports.size} Sportarten löschen`}>
              <Trash2 size={14} /> Löschen ({selectedSports.size})
            </button>
          )}
          <div className="page-controls-spacer" />
          <div className="page-view-toggle">
            <button className={`page-toggle-btn ${viewMode === 'cards' ? 'active' : ''}`} onClick={() => setViewMode('cards')} title="Kacheln">
              <LayoutGrid size={14} />
            </button>
            <button className={`page-toggle-btn ${viewMode === 'list' ? 'active' : ''}`} onClick={() => setViewMode('list')} title="Liste">
              <List size={14} />
            </button>
          </div>
        </div>
        {/* Row 2: Search + record count */}
        <div className="page-controls-row">
          <input
            type="text"
            className="page-search-input"
            placeholder="Sportarten durchsuchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <div className="page-controls-spacer" />
          <span className="page-record-count">{filteredSports.length} / {sports.length} Sportarten</span>
        </div>
      </div>

      {error && (
        <div className="page-error">
          <p>{error}</p>
          <button onClick={loadSports}>Erneut versuchen</button>
        </div>
      )}

      {/* Sports Display */}
      {filteredSports.length === 0 ? (
        <div className="page-empty-state">
          <h3>Keine Sportarten gefunden</h3>
          <p>Versuchen Sie einen anderen Suchbegriff.</p>
        </div>
      ) : viewMode === 'cards' ? (
        <div className="page-cards-grid">
          {filteredSports.map((sport) => (
            <div key={sport.id} className="page-card">
              <div className="page-card-header">
                <h3>
                  {sport.icon && <span>{sport.icon}</span>} {sport.name}
                </h3>
                <span className="page-badge" style={{ backgroundColor: sport.isActive ? '#28a745' : '#dc3545' }}>
                  {sport.isActive ? 'Aktiv' : 'Inaktiv'}
                </span>
              </div>
              <div className="page-card-body">
                {sport.description && <p>{sport.description}</p>}
              </div>
              <div className="page-card-footer">
                <small>Erstellt: {new Date(sport.createdAt).toLocaleDateString('de-DE')}</small>
                <div className="page-card-actions">
                  <button className="page-btn-icon edit" onClick={() => handleEditSport(sport)} title="Bearbeiten">
                    <Pencil size={14} />
                  </button>
                  <button className="page-btn-icon delete" onClick={() => handleDeleteSport(sport)} title="Löschen">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="page-data-panel">
          <table className="page-table">
            <thead>
              <tr>
                {bulkMode && (
                  <th>
                    <input
                      type="checkbox"
                      checked={selectedSports.size === filteredSports.length && filteredSports.length > 0}
                      onChange={handleSelectAll}
                    />
                  </th>
                )}
                <th>Name</th>
                <th>Beschreibung</th>
                <th>Status</th>
                <th>Erstellt</th>
                <th>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {filteredSports.map((sport) => (
                <tr key={sport.id}>
                  {bulkMode && (
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedSports.has(sport.id)}
                        onChange={() => handleSelectSport(sport.id)}
                      />
                    </td>
                  )}
                  <td>
                    {sport.icon && <span>{sport.icon}</span>} {sport.name}
                  </td>
                  <td>{sport.description || '-'}</td>
                  <td>
                    <span className="page-badge" style={{ backgroundColor: sport.isActive ? '#28a745' : '#dc3545' }}>
                      {sport.isActive ? 'Aktiv' : 'Inaktiv'}
                    </span>
                  </td>
                  <td>{new Date(sport.createdAt).toLocaleDateString('de-DE')}</td>
                  <td>
                    <div className="page-table-actions">
                      <button className="page-btn-icon edit" onClick={() => handleEditSport(sport)} title="Bearbeiten">
                        <Pencil size={14} />
                      </button>
                      <button className="page-btn-icon delete" onClick={() => handleDeleteSport(sport)} title="Löschen">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Sport Form Dialog */}
      {showForm && (
        <SportForm
          sport={editingSport}
          onSave={handleSaveSport}
          onCancel={() => {
            setShowForm(false);
            setEditingSport(null);
          }}
          loading={formLoading}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        title="Sportart löschen"
        message={`Möchten Sie die Sportart "${sportToDelete?.name}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`}
        confirmText="Löschen"
        cancelText="Abbrechen"
        onConfirm={confirmDeleteSport}
        onCancel={() => {
          setShowDeleteDialog(false);
          setSportToDelete(null);
        }}
        loading={deleteLoading}
        type="danger"
      />

      {/* Toast Notifications */}
      <ToastContainer toasts={toasts} onRemoveToast={removeToast} />
    </div>
  );
};

export default Sports;
